import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Switch } from "@mui/material";

interface User {
  id: number;
  username: string;
  email: string;
  status: string;
  lastLogin: string;
  industry: string;
  experience: string;
}

interface Activity {
  date: string;
  activity: string;
}

interface Post {
  date: string;
  title: string;
  status: string;
}

interface Permission {
  name: string;
  description: string;
}

function AdminUserDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user }: { user?: User } = location.state || {};

  if (!user) {
    return <div>User not found</div>;
  }

  const recentActivity: Activity[] = [
    { date: "July 17, 2024, 04:00 PM", activity: "Change password" },
    { date: "July 17, 2024, 04:00 PM", activity: "Posted new article" },
    { date: "July 17, 2024, 04:00 PM", activity: "Commented on post" },
  ];

  const userPosts: Post[] = [
    { date: "July 17, 2024, 04:00 PM", title: "Professional Networking tips", status: "Published" },
    { date: "July 17, 2024, 04:00 PM", title: "How to enhance your career", status: "Draft" },
  ];

  const permissions: Permission[] = [
    { name: "View Content", description: "Access all posts, articles, and updates" },
    { name: "Edit Content", description: "Update and refine your own posts and articles" },
    { name: "Create Content", description: "Write and share new posts and articles" },
    { name: "Delete Content", description: "Remove your posts and articles" },
    { name: "Edit Profile", description: "Update your personal and professional information" },
    { name: "View Profile", description: "Control how your profile appears to others" },
    { name: "Access Premium Features", description: "Use exclusive features available to premium members" },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <Avatar sx={{ width: 100, height: 100, mr: 3 }}>{user.username[0]}</Avatar>
          <Box>
            <Typography variant="h5">{user.username}</Typography>
            <Typography variant="caption">
              {user.email} | User ID: {user.id}
            </Typography>
          </Box>
          <Button variant="contained" color="primary" onClick={() => navigate(-1)} sx={{ ml: { sm: "auto" } }}>
            Save changes
          </Button>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, flexDirection: { xs: "column", sm: "row" } }}>
          <Box>
            <Typography variant="subtitle1">Status</Typography>
            <Chip label={user.status} color={user.status === "active" ? "success" : "error"} />
          </Box>
          <Box>
            <Typography variant="subtitle1">Last Logout</Typography>
            <Typography>{user.lastLogin}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1">Industry</Typography>
            <Typography>{user.industry}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1">Experience</Typography>
            <Typography>{user.experience}</Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", gap: 3, mb: 3, flexDirection: { xs: "column", sm: "row" } }}>
        <Paper sx={{ flex: 1, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentActivity.map((activity, index) => (
                  <TableRow key={index}>
                    <TableCell>{activity.date}</TableCell>
                    <TableCell>{activity.activity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Paper sx={{ flex: 1, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            User posts
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userPosts.map((post, index) => (
                  <TableRow key={index}>
                    <TableCell>{post.date}</TableCell>
                    <TableCell>{post.title}</TableCell>
                    <TableCell>{post.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Permissions
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              {permissions.map((permission, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="subtitle1">{permission.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {permission.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Switch defaultChecked={index < 6} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default AdminUserDetails;
